/**
 * The reCAPTCHA key and script the site runs, and the browser object they
 * give us.
 *
 * Since 2026-10-18 this is a reCAPTCHA Enterprise key in the Cloud project
 * `sellwild`: the page loads enterprise.js and mints tokens with
 * `grecaptcha.enterprise.execute`. The classic key it replaced (6Lc3xNIt...,
 * still in NEXT_PUBLIC_RECAPTCHA_SITE_KEY) had returned no scores since
 * 2026-10-07. A site key is public; it ships in every page.
 */
export const RECAPTCHA_SITE_KEY =
	process.env.NEXT_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY || '6Le6o_QtAAAAAF9jMK--iB75On2kYrAM3i1GuhLl';

export const RECAPTCHA_SCRIPT_URL = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;

/**
 * The same script from Google's other domain, for networks that block
 * www.google.com. Prod 2026-10-28..11-04: 33 of the real checkout visitors who
 * never got a score had the google.com script fail with no ad blocker seen.
 */
export const RECAPTCHA_FALLBACK_SCRIPT_URL = `https://www.recaptcha.net/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;

const FALLBACK_SCRIPT_ID = 'recaptcha-fallback';

/**
 * Load the recaptcha.net copy once, after the google.com one failed. Returns
 * false when it was already added (or there is no document).
 */
export function loadRecaptchaFallback(doc: Document | null = typeof document === "undefined" ? null : document): boolean {
	if (!doc || doc.getElementById(FALLBACK_SCRIPT_ID)) return false;
	const script = doc.createElement('script');
	script.id = FALLBACK_SCRIPT_ID;
	script.src = RECAPTCHA_FALLBACK_SCRIPT_URL;
	script.async = true;
	doc.head.appendChild(script);
	return true;
}

/** What enterprise.js puts on `window.grecaptcha.enterprise`, as far as we use it. */
export interface RecaptchaClient {
	execute?: (siteKey: string, options: { action: string }) => Promise<string>;
	ready?: (callback: () => void) => void;
	/** Draws a checkbox widget for another key into `container` (components/BotChallengeHost.tsx). */
	render?: (
		container: HTMLElement,
		params: {
			sitekey: string;
			callback: (token: string) => void;
			'expired-callback'?: () => void;
			'error-callback'?: () => void;
		}
	) => number;
}

/** The Enterprise client, or undefined before enterprise.js has run (or on the server). */
export function recaptchaClient(): RecaptchaClient | undefined {
	if (typeof window === 'undefined') return undefined;
	const grecaptcha = (window as unknown as { grecaptcha?: { enterprise?: RecaptchaClient } }).grecaptcha;
	return grecaptcha?.enterprise ?? undefined;
}
